import {
  Play, Microscope, Database, Network, Brain, BarChart3, Dna,
  CheckCircle, Upload, Zap, Rocket, Shield, Sprout, Leaf,
} from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import CountUp from '../components/animations/CountUp';

const HomePage = ({ setActiveTab }) => {
  return (
    <div className="relative">
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-tint border border-blue-100 text-sm text-brand">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse-glow"></span>
                Genomic Cry toxin discovery platform
              </div>
              <div className="space-y-4">
                <h1 className="font-display text-4xl lg:text-6xl font-extrabold leading-tight">
                  <span className="text-brand">Unveiling the Secrets</span>
                  <br />
                  <span className="text-slate-900">of Cry Toxins</span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  CryMiner identifies, annotates, classifies, and analyzes Cry (crystal) toxin genes from
                  bacterial genomes — combining advanced genomics, comparative sequence analysis, and
                  machine-learning prediction to accelerate the discovery of novel insecticidal proteins
                  for next-generation transgenic crops and sustainable pest management.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => setActiveTab('analysis')} className="brand-btn group px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                  <Play className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  <span>Get Started</span>
                </button>
                <button onClick={() => setActiveTab('about')} className="group px-8 py-4 bg-white border border-brand text-brand rounded-xl font-semibold text-lg hover:bg-brand-tint transition-all duration-300 flex items-center justify-center space-x-2">
                  <Microscope className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Learn More</span>
                </button>
              </div>
            </div>

            <div>
              <div className="relative animate-float">
                <div className="relative bg-white rounded-3xl p-8 border border-slate-200 shadow-xl">
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { Icon: Database, title: 'Genome Mining', sub: '700+ Cry genes' },
                      { Icon: Network, title: 'Auto Classification', sub: 'Family & subtype' },
                      { Icon: Brain, title: 'AI Discovery', sub: 'Novel toxins' },
                      { Icon: BarChart3, title: 'Visual Analytics', sub: 'Trees & genome maps' },
                    ].map(({ Icon, title, sub }, i) => (
                      <div key={i} className="flex items-center space-x-3 group/item">
                        <Icon className="w-8 h-8 text-brand group-hover/item:scale-125 transition-transform duration-300" />
                        <div>
                          <div className="font-semibold text-slate-900">{title}</div>
                          <div className="text-sm text-slate-500">{sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: '700+', label: 'Cry Genes', icon: Dna },
              { value: '99.9%', label: 'Accuracy', icon: CheckCircle },
              { value: '3', label: 'Input Types', icon: Upload },
              { value: '24/7', label: 'Availability', icon: Zap },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Reveal key={index} delay={index * 120}>
                  <div className="text-center group bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-brand-tint group-hover:scale-110 transition-all duration-300">
                      <Icon className="w-8 h-8 text-brand group-hover:rotate-12 transition-transform" />
                    </div>
                    <div className="font-display text-3xl font-bold text-slate-900 mb-2"><CountUp value={stat.value} /></div>
                    <div className="text-slate-500">{stat.label}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why CryMiner */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl font-bold text-slate-900 mb-4">Why <span className="text-brand">CryMiner</span>?</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">Transforming Cry toxin discovery through genomics, bioinformatics, and AI-driven prediction.</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { icon: Rocket, title: 'Accelerate R&D', text: 'Reduce months of manual analysis to hours with automated workflows and high-throughput genomic screening.' },
              { icon: Shield, title: 'Enhance Pest Resistance', text: 'Discover novel Cry proteins capable of overcoming resistance in economically important agricultural pests.' },
              { icon: Sprout, title: 'Support Transgenic Crops', text: 'Identify high-performance toxin candidates for incorporation into genetically engineered crops.' },
              { icon: Leaf, title: 'Improve Biocontrol', text: 'Facilitate development of environmentally friendly biopesticides and microbial control agents.' },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="h-full bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300 group">
                    <div className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-brand-tint mb-4">
                      <Icon className="w-7 h-7 text-brand group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                    <p className="text-slate-600 text-sm">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <div className="bg-brand-tint border border-blue-100 rounded-3xl p-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                {[
                  'Rapid identification of Cry toxin genes',
                  'Discovery of novel insecticidal proteins',
                  'Improved transgenic crop development pipelines',
                  'Enhanced pest management strategies',
                  'Reduced research timelines and costs',
                  'Support for innovation and IP generation',
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start text-slate-700">
                    <CheckCircle className="w-5 h-5 text-brand mr-2 mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;